import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, MapPin } from "lucide-react";

const lineWrap = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } } };
const lineUp = {
  hidden: { y: "110%" },
  show: { y: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

const Line = ({ children, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span variants={lineUp} className={`block ${className}`}>{children}</motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen brushed-metal grain overflow-hidden text-white" data-testid="hero">
      {/* metal texture wash */}
      <motion.div style={{ scale }} className="absolute inset-0 opacity-[0.14] mix-blend-screen">
        <img
          src="https://images.unsplash.com/photo-1656055448548-e26753307fc7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxzaWx2ZXIlMjBicnVzaGVkJTIwbWV0YWwlMjB0ZXh0dXJlfGVufDB8fHx8MTc4NjE3NTgxOXww&ixlib=rb-4.1.0&q=85"
          alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />

      <motion.div style={{ y, opacity }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-40 pb-24 min-h-screen flex flex-col justify-center">
        <motion.div variants={lineWrap} initial="hidden" animate="show">
          <Line className="font-grotesk text-xs sm:text-sm tracking-[0.35em] uppercase text-neutral-400 mb-8">
            <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-[var(--pmr-red)]" /> Dachau & Umgebung · nur Hausbesuche</span>
          </Line>

          <h1 className="font-display font-black tracking-tight leading-[0.92] text-6xl sm:text-8xl lg:text-[10rem]">
            <Line className="silver-text">P.M.R.</Line>
          </h1>

          <div className="mt-4 sm:mt-6 font-grotesk font-medium text-2xl sm:text-4xl lg:text-5xl text-neutral-200 leading-[1.05] max-w-4xl">
            <Line>Private Physiotherapie,</Line>
            <Line className="text-neutral-400">die zu Ihnen nach Hause kommt.</Line>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 max-w-xl font-body text-base sm:text-lg text-neutral-400 leading-relaxed"
        >
          Krankengymnastik, Manuelle Therapie, Massage und Lymphdrainage – individuell,
          diskret und ohne Wartezimmer. Timo Roider behandelt Sie in Ihrer gewohnten Umgebung.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a href="#kontakt" data-testid="hero-cta-book"
            className="font-grotesk font-medium inline-flex items-center justify-center gap-2 rounded-full bg-[var(--pmr-red)] hover:bg-[var(--pmr-red-deep)] text-white px-8 py-4 text-base transition-colors">
            Termin anfragen
          </a>
          <a href="#leistungen" data-testid="hero-cta-services"
            className="font-grotesk font-medium inline-flex items-center justify-center gap-2 rounded-full border border-white/25 hover:border-white/60 hover:bg-white/5 text-white px-8 py-4 text-base transition-colors">
            Leistungen ansehen
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-neutral-500">
        <span className="font-grotesk text-[10px] tracking-[0.3em] uppercase">Scrollen</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
};
